import type {
  SkillAverage,
  ScoreBandDistribution,
  NeedsAttentionRider,
  SkillsReportRider,
  SkillsReportGroup,
  SkillsReportResponse,
} from '../types/skills';

/**
 * Compute the mean at_target_percent (Advanced-only) across all skill averages.
 * Returns 0 for an empty list. Rounded to nearest integer.
 *
 * Requirements: 3.1
 */
export function computeGroupAverage(skillAverages: SkillAverage[]): number {
  if (skillAverages.length === 0) return 0;
  const sum = skillAverages.reduce((acc, s) => acc + s.at_target_percent, 0);
  return Math.round(sum / skillAverages.length);
}

/**
 * Count riders per readiness suggestion.
 * Sum of all counts === riders.length.
 *
 * Requirements: 3.4
 */
export function computeReadinessSummary(riders: SkillsReportRider[]): Record<string, number> {
  const summary: Record<string, number> = {};
  for (const rider of riders) {
    const suggestion = rider.group_readiness.suggestion;
    summary[suggestion] = (summary[suggestion] || 0) + 1;
  }
  return summary;
}

/**
 * Return up to 3 skills with the lowest weighted_skill_score.
 * Sorted by weighted_skill_score ascending, with alphabetical skill_name tiebreaker.
 *
 * Requirements: 3.2, 3.3, 3.4
 */
export function computeFocusAreas(skillAverages: SkillAverage[]): SkillAverage[] {
  return [...skillAverages]
    .sort((a, b) => {
      if (a.weighted_skill_score !== b.weighted_skill_score) {
        return a.weighted_skill_score - b.weighted_skill_score;
      }
      return a.skill_name.localeCompare(b.skill_name);
    })
    .slice(0, 3);
}

/**
 * Classify riders into score bands.
 * Below: skills_percentage < 50
 * Mid: 50 <= skills_percentage < 100
 * Above: skills_percentage >= 100
 *
 * Requirements: 6.1, 6.2, 6.3, 8.2
 */
export function computeScoreBandDistribution(riders: SkillsReportRider[]): ScoreBandDistribution {
  let belowTarget = 0;
  let mostAtTarget = 0;
  let allAtTarget = 0;

  for (const rider of riders) {
    if (rider.skills_percentage >= 100) {
      allAtTarget++;
    } else if (rider.skills_percentage >= 50) {
      mostAtTarget++;
    } else {
      belowTarget++;
    }
  }

  return { belowTarget, mostAtTarget, allAtTarget };
}

/**
 * Filter riders who need attention:
 * - assessed_count < total_skills * 0.5
 *
 * Requirements: 8.2, 9.3
 */
export function computeNeedsAttentionRiders(riders: SkillsReportRider[]): NeedsAttentionRider[] {
  const result: NeedsAttentionRider[] = [];

  for (const rider of riders) {
    const isUnderAssessed = rider.assessed_count < rider.total_skills * 0.5;

    if (isUnderAssessed) {
      result.push({
        rider_id: rider.rider_id,
        name: rider.name,
        reason: `Only ${rider.assessed_count}/${rider.total_skills} skills assessed`,
      });
    }
  }

  return result;
}

/**
 * Sort riders by skills_score descending, then name ascending (case-insensitive).
 * Returns a new array (does not mutate input).
 *
 * Requirements: 4.1
 */
export function sortRidersForDisplay(riders: SkillsReportRider[]): SkillsReportRider[] {
  return [...riders].sort((a, b) => {
    if (b.skills_score !== a.skills_score) {
      return b.skills_score - a.skills_score;
    }
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
}

/**
 * Find a group in the report by group_name.
 *
 * Requirements: 1.1, 1.4, 2.2
 */
export function getGroupFromReport(
  report: SkillsReportResponse,
  groupName: string,
): SkillsReportGroup | undefined {
  return report.groups.find((g) => g.group_name === groupName);
}

/**
 * Get the default group: the one with the lowest group_order.
 *
 * Requirements: 2.3
 */
export function getDefaultGroup(groups: SkillsReportGroup[]): SkillsReportGroup | undefined {
  if (groups.length === 0) return undefined;
  return groups.reduce((min, g) => (g.group_order < min.group_order ? g : min));
}

/**
 * Determine heatmap Tailwind color classes from at_target_percent.
 * Green: >= 75%, Yellow: 50-74%, Red: < 50%
 *
 * Requirements: 5.4
 */
export function getHeatmapColor(atTargetPercent: number): string {
  if (atTargetPercent >= 75) return 'bg-green-100 text-green-800';
  if (atTargetPercent >= 50) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}

/**
 * Group skill_averages by their section field.
 *
 * Requirements: 5.1, 5.3
 */
export function groupSkillsBySection(skillAverages: SkillAverage[]): Map<string, SkillAverage[]> {
  const map = new Map<string, SkillAverage[]>();
  for (const skill of skillAverages) {
    const existing = map.get(skill.section);
    if (existing) {
      existing.push(skill);
    } else {
      map.set(skill.section, [skill]);
    }
  }
  return map;
}
